import { useMemo, useRef } from "react";
import { segmentUrl } from "../api.js";
import Icon from "./Icon.jsx";

// Layers panel on the left. Top of the list = top of the z-order. Drag a
// row up/down to reorder. The state's `layers` array is rendered bottom-
// to-top in Konva, so we display it reversed.

export default function LayersPanel({
  layers,
  groups,
  selectedIds,
  onSelectLayers,
  onToggleSelection,
  onRemoveLayers,
  onReorder,
  onCreateGroup,
  onUngroup,
  onToggleCollapse,
  onSetLayerLocked,
  onSetLayerHidden,
  onLayerContextMenu,
  onGroupContextMenu,
}) {
  const dragIdxRef = useRef(null);
  const groupsById = useMemo(
    () => Object.fromEntries(groups.map((g) => [g.id, g])),
    [groups],
  );

  const rows = useMemo(() => {
    const out = [];
    const seenGroups = new Set();
    for (let i = layers.length - 1; i >= 0; i--) {
      const layer = layers[i];
      if (layer.groupId) {
        const group = groupsById[layer.groupId];
        if (!seenGroups.has(layer.groupId)) {
          seenGroups.add(layer.groupId);
          if (group) {
            out.push({ kind: "groupHeader", group, layerCount: 0, anchorIdx: i });
          }
        }
        if (group?.collapsed) continue;
        out.push({ kind: "layer", layer, idx: i, indented: true });
      } else {
        out.push({ kind: "layer", layer, idx: i, indented: false });
      }
    }
    const memberCounts = new Map();
    for (const l of layers) {
      if (l.groupId) memberCounts.set(l.groupId, (memberCounts.get(l.groupId) || 0) + 1);
    }
    for (const r of out) {
      if (r.kind === "groupHeader") r.layerCount = memberCounts.get(r.group.id) || 0;
    }
    return out;
  }, [layers, groupsById]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const isLayerSelected = (id) => selectedSet.has(id);
  const isGroupSelected = (group) => {
    const memberIds = layers.filter((l) => l.groupId === group.id).map((l) => l.id);
    return memberIds.length > 0 && memberIds.every((id) => selectedSet.has(id));
  };

  const handleDragStart = (idx) => (e) => {
    dragIdxRef.current = idx;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  };
  const handleDragOver = (e) => {
    if (dragIdxRef.current == null) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (toIdx) => (e) => {
    e.preventDefault();
    const fromIdx = dragIdxRef.current;
    dragIdxRef.current = null;
    if (fromIdx == null || fromIdx === toIdx) return;
    onReorder(fromIdx, toIdx);
  };

  const canGroup =
    selectedIds.length >= 2 &&
    selectedIds.every((id) => {
      const l = layers.find((x) => x.id === id);
      return l && !l.groupId;
    });
  const ungroupCandidate = (() => {
    if (selectedIds.length === 0) return null;
    const groupIds = new Set(
      selectedIds.map((id) => layers.find((l) => l.id === id)?.groupId).filter(Boolean),
    );
    if (groupIds.size !== 1) return null;
    return Array.from(groupIds)[0];
  })();

  return (
    <aside className="flex h-full min-h-0 flex-col border-r-2 border-paper-edge bg-paper">
      <div className="shrink-0 border-b-2 border-paper-edge px-4 pt-3 pb-3">
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-bold tracking-tight text-ink">Layers</h2>
          <span className="text-xs font-medium text-ink-soft">
            {layers.length} · {groups.length} group{groups.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="mt-2 flex gap-2">
          <IconButton
            icon="create-group"
            label="Group"
            disabled={!canGroup}
            onClick={() => onCreateGroup(selectedIds)}
            title={canGroup ? "Group selected layers" : "Select 2+ ungrouped layers"}
          />
          <IconButton
            icon="ungroup"
            label="Ungroup"
            disabled={!ungroupCandidate}
            onClick={() => ungroupCandidate && onUngroup(ungroupCandidate)}
            title={ungroupCandidate ? "Ungroup the selection" : "Select layers in a single group"}
          />
        </div>
      </div>

      <ul className="min-h-0 flex-1 overflow-y-auto py-1.5" onDragOver={handleDragOver}>
        {rows.length === 0 && (
          <li className="px-4 py-6 text-center text-sm text-ink-soft">
            Drag a tile from the palette into the canvas.
          </li>
        )}
        {rows.map((row, displayIdx) => {
          if (row.kind === "groupHeader") {
            const g = row.group;
            const groupSelected = isGroupSelected(g);
            const memberLayers = layers.filter((l) => l.groupId === g.id);
            const memberIds = memberLayers.map((l) => l.id);
            const allHidden = memberLayers.length > 0 && memberLayers.every((l) => l.hidden);
            const allLocked = memberLayers.length > 0 && memberLayers.every((l) => l.locked);
            return (
              <li
                key={`grp-${g.id}`}
                onClick={(e) => {
                  const memberIds = layers.filter((l) => l.groupId === g.id).map((l) => l.id);
                  if (e.metaKey || e.ctrlKey) {
                    if (memberIds.every((id) => selectedSet.has(id))) {
                      onSelectLayers(selectedIds.filter((id) => !memberIds.includes(id)));
                    } else {
                      onSelectLayers([...new Set([...selectedIds, ...memberIds])]);
                    }
                  } else {
                    onSelectLayers(memberIds);
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  // Bring this group's members into selection so menu
                  // actions operate on the right thing.
                  const memberIds = layers.filter((l) => l.groupId === g.id).map((l) => l.id);
                  onSelectLayers(memberIds);
                  if (onGroupContextMenu) {
                    onGroupContextMenu({ groupId: g.id, x: e.clientX, y: e.clientY });
                  }
                }}
                className={
                  "mx-2 mb-1 flex cursor-pointer items-center gap-2 rounded-xl border-2 px-2 py-1.5 text-sm transition " +
                  (groupSelected
                    ? "border-ink bg-cyan-soft"
                    : "border-transparent hover:border-paper-edge hover:bg-paper-deep")
                }
              >
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleCollapse(g.id); }}
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-ink hover:bg-paper-edge"
                  title={g.collapsed ? "Expand" : "Collapse"}
                >
                  {g.collapsed ? "▸" : "▾"}
                </button>
                <Icon name="create-group" size={22} alt="" />
                <span className="flex-1 truncate font-semibold text-ink">{g.name}</span>
                <span className="rounded-full bg-paper-deep px-2 py-0.5 text-[11px] font-semibold tabular-nums text-ink-soft">
                  {row.layerCount}
                </span>
                <ToggleIconButton
                  active={!allHidden}
                  inactive={allHidden}
                  onClick={(e) => { e.stopPropagation(); onSetLayerHidden(memberIds, allHidden ? false : true); }}
                  title={allHidden ? "Show group" : "Hide group"}
                >
                  <Icon name={allHidden ? "eye-closed" : "eye-open"} size={20} alt="" />
                </ToggleIconButton>
                <ToggleIconButton
                  active={!allLocked}
                  inactive={allLocked}
                  onClick={(e) => { e.stopPropagation(); onSetLayerLocked(memberIds, allLocked ? false : true); }}
                  title={allLocked ? "Unlock group" : "Lock group"}
                >
                  <Icon name={allLocked ? "locked" : "unlocked"} size={20} alt="" />
                </ToggleIconButton>
              </li>
            );
          }
          return (
            <LayerRow
              key={row.layer.id}
              layer={row.layer}
              storageIdx={row.idx}
              indented={row.indented}
              isSelected={isLayerSelected(row.layer.id)}
              onToggleLocked={() =>
                onSetLayerLocked([row.layer.id], !row.layer.locked)
              }
              onToggleHidden={() =>
                onSetLayerHidden([row.layer.id], !row.layer.hidden)
              }
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey) {
                  onToggleSelection(row.layer.id);
                } else if (e.shiftKey && selectedIds.length > 0) {
                  const lastDisplayIdx = displayIdx;
                  const firstSelectedDisplayIdx = rows.findIndex(
                    (r) => r.kind === "layer" && r.layer.id === selectedIds[selectedIds.length - 1],
                  );
                  if (firstSelectedDisplayIdx >= 0) {
                    const lo = Math.min(firstSelectedDisplayIdx, lastDisplayIdx);
                    const hi = Math.max(firstSelectedDisplayIdx, lastDisplayIdx);
                    const range = rows.slice(lo, hi + 1)
                      .filter((r) => r.kind === "layer")
                      .map((r) => r.layer.id);
                    onSelectLayers([...new Set([...selectedIds, ...range])]);
                  } else {
                    onSelectLayers([row.layer.id]);
                  }
                } else {
                  onSelectLayers([row.layer.id]);
                }
              }}
              onRemove={() => onRemoveLayers([row.layer.id])}
              onDragStart={handleDragStart(row.idx)}
              onDragOver={handleDragOver}
              onDrop={handleDrop(row.idx)}
              onContextMenu={(e) => {
                e.preventDefault();
                // If the row isn't already in the selection, replace
                // selection with just it; otherwise leave the multi-
                // selection intact so the menu acts on all of them.
                if (!selectedSet.has(row.layer.id)) onSelectLayers([row.layer.id]);
                if (onLayerContextMenu) {
                  onLayerContextMenu({ layerId: row.layer.id, x: e.clientX, y: e.clientY });
                }
              }}
            />
          );
        })}
      </ul>
    </aside>
  );
}

function LayerRow({
  layer,
  isSelected,
  indented,
  onClick,
  onRemove,
  onToggleLocked,
  onToggleHidden,
  onDragStart,
  onDragOver,
  onDrop,
  onContextMenu,
}) {
  const head = layer.members[0];
  return (
    <li
      draggable={!layer.locked}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onClick={onClick}
      onContextMenu={onContextMenu}
      className={
        "mb-1 flex items-center gap-2 rounded-xl border-2 px-2 py-1.5 text-sm transition " +
        (indented ? "ml-6 mr-2" : "mx-2") +
        " " +
        (layer.hidden ? "opacity-50 " : "") +
        (isSelected
          ? "border-ink bg-cyan-soft text-ink shadow-card cursor-pointer"
          : "border-transparent hover:border-paper-edge hover:bg-paper-deep cursor-pointer")
      }
    >
      <div
        className={
          "flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 border-paper-edge bg-checker-sm " +
          (layer.hidden ? "grayscale" : "")
        }
      >
        {head && (
          <img
            src={segmentUrl(head.file)}
            alt=""
            className="max-h-full max-w-full object-contain"
            draggable={false}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        {layer.members.length > 1 && (
          <div className={"text-xs font-semibold text-ink-soft " + (layer.hidden ? "line-through" : "")}>
            {layer.members.length} pieces
          </div>
        )}
      </div>
      <ToggleIconButton
        active={!layer.hidden}
        inactive={layer.hidden}
        onClick={(e) => { e.stopPropagation(); onToggleHidden(); }}
        title={layer.hidden ? "Show layer" : "Hide layer"}
      >
        <Icon name={layer.hidden ? "eye-closed" : "eye-open"} size={20} alt="" />
      </ToggleIconButton>
      <ToggleIconButton
        active={!layer.locked}
        inactive={layer.locked}
        onClick={(e) => { e.stopPropagation(); onToggleLocked(); }}
        title={layer.locked ? "Unlock layer" : "Lock layer"}
      >
        <Icon name={layer.locked ? "locked" : "unlocked"} size={20} alt="" />
      </ToggleIconButton>
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(); }}
        title="Delete layer"
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg hover:bg-coral-soft"
        aria-label="Delete layer"
      >
        <Icon name="delete" size={20} alt="Delete" />
      </button>
    </li>
  );
}

// ---- Toggle button + inline SVGs (matching the chunky icon style) -------

function ToggleIconButton({ children, onClick, title, active, inactive }) {
  let cls = "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg transition ";
  if (inactive) cls += "opacity-40 hover:bg-paper-deep hover:opacity-100";
  else cls += "hover:bg-cyan-soft";
  return (
    <button onClick={onClick} title={title} aria-label={title} className={cls}>
      {children}
    </button>
  );
}

function IconButton({ icon, label, onClick, disabled, title }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border-2 border-ink/20 bg-white px-2 py-1.5 text-xs font-semibold text-ink transition hover:border-ink hover:bg-cyan-soft disabled:opacity-40 disabled:hover:border-ink/20 disabled:hover:bg-white"
    >
      <Icon name={icon} size={20} alt="" />
      {label}
    </button>
  );
}
